import React, { createContext, useContext, useEffect, useState } from 'react';
import { ErrorMessage, Field, Form, Formik } from 'formik';
import Grid from '@material-ui/core/Grid';
import CardContent from '@material-ui/core/CardContent';
import {
  Card,
  CardActions,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Typography
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import CardHeader from '@material-ui/core/CardHeader';
import { useDispatch, useSelector } from 'react-redux';
import red from '@material-ui/core/colors/red';
import { replaceNullToEmpty } from '../../helpers';
import InputErrorMessage from '../InputErrorMessage';
import UploadRxFormFile from '../../pages/RxForms/components/UploadRxFormFile';
import { decodeFormikValues, encodeFormikValues } from './helpers';
import RenderedControl from '../../pages/Account/components/RxFormCustomization/FormBuilder/controls/RenderedControl';
import { createLabel } from '../../pages/Account/components/RxFormCustomization/FormBuilder/controls/helpers';
import {
  CONTROL_FILE,
  LAYOUT_DIVIDER
} from '../../pages/Account/components/RxFormCustomization/FormBuilder/helpers';
import { ACCEPTED_FILE_TYPES, FILE_TYPE_IDS } from '../../constants';
import hasPermission from '../../selectors/hasPermission';
import { fetchOrganizationUsers } from '../../reducers/organizationUsers';

const useStyles = makeStyles(theme => ({
  form: {
    width: '100%',
    marginTop: 0
  },
  hidden: {
    display: 'none'
  },
  control: { padding: theme.spacing(0.5) },
  section: {
    marginBottom: theme.spacing(0.5)
  },
  divider: {
    marginTop: theme.spacing(1.5),
    marginBottom: theme.spacing(1.5)
  },
  betaLabel: {
    color: red[400],
    fontSize: '0.75rem',
    verticalAlign: 'super'
  }
}));

const Context = createContext({ only: [], hiddenFields: [] });

const createExcludeChecker = (only, hiddenFields) => name => {
  return (only.length > 0 && only.indexOf(name) === -1) || hiddenFields.indexOf(name) !== -1;
};

export const GridField = ({ show, name, skipOnly, isCustomField, children, ...props }) => {
  const classes = useStyles();
  const { only, hiddenFields } = useContext(Context);

  const isExcluded = createExcludeChecker(only, hiddenFields);

  if (!skipOnly && isExcluded(name)) {
    return null;
  }

  const overrideProps = { ...props, xs: 12, md: 12, lg: 12 };

  return (
    <Grid item {...overrideProps} className={!show ? classes.hidden : classes.control}>
      {children}
    </Grid>
  );
};

GridField.defaultProps = {
  show: true,
  skipOnly: false,
  isCustomField: false
};

GridField.propTypes = {
  name: PropTypes.string.isRequired,
  children: PropTypes.element.isRequired,
  show: PropTypes.bool,
  skipOnly: PropTypes.bool,
  isCustomField: PropTypes.bool
};

const hasUpdatableFields = (section, only) => {
  return (
    section.children.filter(col => {
      return (
        col.children.filter(field => {
          return only.indexOf(field.system_name) !== -1;
        }).length > 0
      );
    }).length > 0
  );
};

const getDoctors = members => {
  return members.map(user => {
    return {
      id: user.id,
      full_name: user.full_name,
      role: user.pivot.role.name
    };
  });
};

const SAVED_FOR_LATER = 'saved_for_later';

const RxForm = ({
  organization,
  title,
  description,
  preference,
  hiddenFields,
  thirdPartyPartnerName,
  onSubmit,
  isLoading,
  rxForm,
  availableLocations,
  only,
  actions,
  doctors: doctorsProp,
  pricing,
  formStructure,
  newControls,
  saveForLaterEnabled,
  nativeFields,
  createValidationSchema,
  getCustomFieldOverride,
  ...props
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const organizationMembers = useSelector(state => state.organizationUsers.organizations);
  const canSelectDoctor = useSelector(state =>
    hasPermission(state, {
      permissions: [
        'rx-forms.manage',
        'rx-forms.select-doctor',
        'organization.rx-forms.select-doctor'
      ],
      organizationId: organization.id
    })
  );

  const [doctors, setDoctors] = useState({
    list: [],
    enabled: canSelectDoctor,
    fetching: false
  });

  useEffect(() => {
    setDoctors(current => ({
      ...current,
      enabled: canSelectDoctor
    }));
  }, [canSelectDoctor]);

  useEffect(() => {
    if (!canSelectDoctor) {
      return;
    }
    if (organization) {
      if (organization.users) {
        setDoctors(current => ({
          ...current,
          list: getDoctors(organization.users)
        }));
        return;
      }

      if (organizationMembers[organization.id]) {
        if (organizationMembers[organization.id].fetching) {
          setDoctors(current => ({
            ...current,
            fetching: true,
            list: []
          }));
        } else if (organizationMembers[organization.id].users) {
          setDoctors(current => ({
            ...current,
            fetching: false,
            list: organizationMembers[organization.id].users
          }));
        }
      } else if (
        !organizationMembers[organization.id] ||
        !organizationMembers[organization.id].fetching
      ) {
        dispatch(fetchOrganizationUsers(organization.id));
      }
    }
  }, [organizationMembers, organization, setDoctors, dispatch, canSelectDoctor]);

  const withDoctorSelection = doctors.enabled;
  const overrideOptions = {
    requireDoctor: withDoctorSelection
  };
  const isManagement = useSelector(state => state.auth.isManagement);
  const validationSchema = createValidationSchema(only, overrideOptions, newControls);

  const encodedFormikValues = encodeFormikValues(rxForm);
  const initialValues = replaceNullToEmpty(encodedFormikValues, ['bonding_date']);

  const renderFormBuilderField = ({
    id,
    system_type: systemType,
    system_name: name,
    custom_system_name: customSystemName,
    props: fieldProps,
    rules,
    setFieldValue,
    setFieldTouched,
    values,
    context_help: contextHelp
  }) => {
    const isCustomField = systemType !== 'existing';
    const OverrideComponent = getCustomFieldOverride
      ? getCustomFieldOverride(customSystemName)
      : null;

    if (OverrideComponent) {
      return (
        <GridField key={id} name={name} isCustomField={isCustomField}>
          <Field
            id={name}
            name={name}
            props={{ ...fieldProps }}
            rules={rules}
            system_type={systemType}
            contextHelp={contextHelp}
            component={OverrideComponent}
          />
        </GridField>
      );
    }

    if (systemType === CONTROL_FILE) {
      const label = createLabel(fieldProps, rules);

      return (
        <GridField key={name} name={name} isCustomField={isCustomField}>
          <FormControl component="fieldset" fullWidth>
            <FormLabel component="legend">{label}</FormLabel>
            <UploadRxFormFile
              accept={ACCEPTED_FILE_TYPES[FILE_TYPE_IDS.other]}
              name={name}
              preferenceValue={[]}
              title={label}
              onChange={files => {
                setTimeout(() => setFieldValue(name, files));
                setFieldTouched(name, true, false);
              }}
              values={values[name]}
              label="Add Files"
              maxFiles={10}
              organization={organization}
              autoExtractCompressedFiles={null}
              existingFiles={values[name]}
              preventDuplicate
            />
            <ErrorMessage
              name={name}
              render={message => <InputErrorMessage>{message}</InputErrorMessage>}
            />
          </FormControl>
        </GridField>
      );
    }
    return (
      <GridField key={id} name={name} isCustomField={isCustomField}>
        <Field
          id={name}
          name={name}
          props={{ ...fieldProps }}
          rules={rules}
          system_type={systemType}
          contextHelp={contextHelp}
          component={RenderedControl}
        />
      </GridField>
    );
  };

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      validationSchema={validationSchema}
      onSubmit={(data, ...args) => {
        const decodedValues = decodeFormikValues(data);
        return onSubmit(decodedValues, ...args);
      }}
    >
      {({
        submitForm,
        isValid,
        status,
        dirty,
        values,
        setFieldValue,
        isSubmitting,
        setFieldTouched,
        errors
      }) => {
        const decodedValues = decodeFormikValues(values);
        const existingFieldsMap = nativeFields({
          withDoctorSelection,
          doctors,
          preference,
          thirdPartyPartnerName,
          isSubmitting,
          decodedValues,
          pricing,
          saveForLaterEnabled,
          values,
          isManagement,
          setFieldValue,
          setFieldTouched,
          organization,
          rxForm,
          initialValues,
          availableLocations,
          GridField
        });

        let previousSectionHasNoUpdatableFields = false;
        return (
          <Card {...props} id="main-rxform">
            <CardHeader title={title} subheader={description} />
            <CardContent>
              <Context.Provider value={{ only, hiddenFields }}>
                <Form className={classes.form} noValidate>
                  {formStructure.map(section => {
                    const colSize = 12 / section.children.length;

                    if (
                      !previousSectionHasNoUpdatableFields &&
                      section.system_type === LAYOUT_DIVIDER
                    ) {
                      return <Divider className={classes.divider} />;
                    }

                    if (!hasUpdatableFields(section, only)) {
                      previousSectionHasNoUpdatableFields = true;
                      return '';
                    }
                    previousSectionHasNoUpdatableFields = false;

                    return (
                      <Grid
                        container
                        spacing={1}
                        key={`section-${section.id}`}
                        className={classes.section}
                      >
                        {section.children.map(col => {
                          const itemKey = `column-${col.id}`;
                          return (
                            <Grid item xs={12} key={itemKey} md={colSize}>
                              {col.children.map(field => {
                                const comp = existingFieldsMap[field.system_name];
                                const matchedControl = newControls.find(
                                  control => control.system_name === field.system_name
                                );

                                if (!comp && !matchedControl) {
                                  return '';
                                }
                                return (
                                  comp ||
                                  renderFormBuilderField({
                                    ...matchedControl,
                                    ...field,
                                    custom_system_name:
                                      matchedControl.custom_system_name || field.custom_system_name,
                                    decodedValues,
                                    setFieldValue,
                                    setFieldTouched,
                                    values
                                  })
                                );
                              })}
                            </Grid>
                          );
                        })}
                      </Grid>
                    );
                  })}
                </Form>
              </Context.Provider>
            </CardContent>
            <Divider />
            {(process.env.REACT_APP_RX_FORM_VALIDATION_ERROR_LOG_ENABLED || 'false') === 'true' && (
              <pre>{JSON.stringify(errors, null, 2)}</pre>
            )}
            <CardActions style={{ justifyContent: 'flex-end' }}>
              {saveForLaterEnabled && !hiddenFields.includes(SAVED_FOR_LATER) && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={values[SAVED_FOR_LATER]}
                      onChange={event => {
                        setFieldValue(SAVED_FOR_LATER, event.target.checked);
                      }}
                      color="primary"
                    />
                  }
                  label={
                    <Typography>
                      Save for later{' '}
                      <Typography component="span" className={classes.betaLabel}>
                        (beta)
                      </Typography>
                    </Typography>
                  }
                />
              )}
              {actions({ dirty, status, isValid, submitForm, setFieldValue, values })}
            </CardActions>
          </Card>
        );
      }}
    </Formik>
  );
};

RxForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  rxForm: PropTypes.object,
  // eslint-disable-next-line react/forbid-prop-types
  availableLocations: PropTypes.array,
  // eslint-disable-next-line react/forbid-prop-types
  preference: PropTypes.object.isRequired,
  thirdPartyPartnerName: PropTypes.string,
  title: PropTypes.string.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  description: PropTypes.any.isRequired,
  only: PropTypes.arrayOf(PropTypes.string),
  actions: PropTypes.func.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  organization: PropTypes.object.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  pricing: PropTypes.object,
  // eslint-disable-next-line react/forbid-prop-types
  newControls: PropTypes.array,
  // eslint-disable-next-line react/forbid-prop-types
  formStructure: PropTypes.array,
  hiddenFields: PropTypes.arrayOf(PropTypes.string),
  saveForLaterEnabled: PropTypes.bool,
  nativeFields: PropTypes.func.isRequired,
  createValidationSchema: PropTypes.func.isRequired,
  getCustomFieldOverride: PropTypes.func,
  doctors: PropTypes.shape({
    enabled: PropTypes.bool.isRequired,
    list: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number.isRequired,
        full_name: PropTypes.string.isRequired,
        role: PropTypes.string.isRequired
      })
    ).isRequired,
    fetching: PropTypes.bool.isRequired
  })
};

RxForm.defaultProps = {
  rxForm: null,
  availableLocations: [],
  thirdPartyPartnerName: null,
  only: [],
  doctors: {
    enabled: false,
    list: [],
    fetching: false
  },
  pricing: null,
  hiddenFields: [],
  saveForLaterEnabled: false,
  formStructure: [],
  newControls: [],
  getCustomFieldOverride: null
};

export default RxForm;
